import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import QuoteStatusIndicator from '../../../components/ui/QuoteStatusIndicator';

const QuoteViewModal = ({ quote, isOpen, onClose, onResendEmail, onDuplicate }) => {
  if (!isOpen || !quote) return null;

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    })?.format(amount);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-black bg-opacity-50"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-surface shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <h3 className="text-xl font-semibold text-text-primary">
                Devis {quote?.quoteNumber}
              </h3>
              <QuoteStatusIndicator status={quote?.status} />
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={onClose}
            />
          </div>

          {/* Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Client Information */}
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-text-primary">Informations client</h4>
              <div className="bg-muted rounded-lg p-4 space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-primary-foreground">
                      {quote?.client?.name?.charAt(0)}{quote?.client?.surname?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <div className="font-medium text-text-primary">
                      {quote?.client?.name} {quote?.client?.surname}
                    </div>
                    <div className="text-sm text-text-secondary">
                      {quote?.client?.category}
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <Icon name="Mail" size={14} className="text-text-secondary" />
                    <span>{quote?.client?.email}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Phone" size={14} className="text-text-secondary" />
                    <span>{quote?.client?.phone}</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon name="MapPin" size={14} className="text-text-secondary mt-0.5" />
                    <span>{quote?.client?.address}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quote Details */}
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-text-primary">Détails du devis</h4>
              <div className="bg-muted rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-text-secondary">Date de création:</span>
                    <div className="font-medium text-text-primary">
                      {formatDate(quote?.createdAt)}
                    </div>
                  </div>
                  <div>
                    <span className="text-text-secondary">Validité:</span>
                    <div className="font-medium text-text-primary">
                      {formatDate(quote?.validUntil)}
                    </div>
                  </div>
                  <div>
                    <span className="text-text-secondary">Style cuisine:</span>
                    <div className="font-medium text-text-primary">
                      {quote?.kitchenStyle}
                    </div>
                  </div>
                  <div>
                    <span className="text-text-secondary">Type plan de travail:</span>
                    <div className="font-medium text-text-primary">
                      {quote?.worktopType}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quote Items */}
          <div className="mt-6">
            <h4 className="text-lg font-medium text-text-primary mb-4">Éléments du devis</h4>
            <div className="bg-muted rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-background">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-text-secondary">
                      Description
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-text-secondary">
                      Quantité
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">
                      Prix unitaire
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-text-secondary">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {quote?.items?.map((item, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-sm text-text-primary">
                        {item?.description}
                      </td>
                      <td className="px-4 py-3 text-sm text-center text-text-primary">
                        {item?.quantity} {item?.unit}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-text-primary">
                        {formatAmount(item?.unitPrice)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-text-primary">
                        {formatAmount(item?.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-background">
                  <tr>
                    <td colSpan="3" className="px-4 py-3 text-right text-sm font-medium text-text-primary">
                      Sous-total HT:
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-text-primary">
                      {formatAmount(quote?.subtotal)}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="3" className="px-4 py-3 text-right text-sm font-medium text-text-primary">
                      TVA ({quote?.vatRate}%):
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-text-primary">
                      {formatAmount(quote?.vatAmount)}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="3" className="px-4 py-3 text-right text-lg font-semibold text-text-primary">
                      Total TTC:
                    </td>
                    <td className="px-4 py-3 text-right text-lg font-semibold text-primary">
                      {formatAmount(quote?.totalAmount)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-border">
            <Button
              variant="outline"
              iconName="Copy"
              iconPosition="left"
              onClick={() => onDuplicate(quote)}
            >
              Dupliquer
            </Button>
            <Button
              variant="outline"
              iconName="Mail"
              iconPosition="left"
              onClick={() => onResendEmail(quote)}
            >
              Renvoyer par email
            </Button>
            <Button
              variant="default"
              iconName="Download"
              iconPosition="left"
            >
              Télécharger PDF
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteViewModal;